import { z } from 'zod';

/**
 * What a skin's effects are allowed to be (ADR 0041).
 *
 * ## Declarative, never executable
 *
 * A descriptor is DATA. There is no field that names a function, a module, a
 * shader, a pipeline or a callback, and there is no free-form object anywhere:
 * every schema in this file is `.strict()`, so an unknown key is a validation
 * error rather than something the runtime might one day honour. A skin is
 * content the owner supplies, and content must not be able to run.
 *
 * ## Every number has a ceiling
 *
 * Not because a value might look wrong, but because six of these are on screen
 * at once in a 3x3 and the arena has a frame budget somebody measured. A
 * descriptor with an uncapped emission rate is a descriptor that can make the
 * duel unplayable, so there is no such field: every rate, count, lifespan and
 * size is bounded here and the bound is part of the contract.
 *
 * ## Two effects are MANDATORY
 *
 * Every enabled skin has particles over its body and a trail while it moves.
 * That is the product decision, and it is expressed as two required fields
 * rather than as a rule somebody has to remember - a descriptor without either
 * one does not parse.
 *
 * ## What a skin can never touch
 *
 * Nothing here reaches the hitbox, the damage, the movement, the targeting or
 * the economy. There is no field for any of them, which is stronger than a rule
 * against using one. Sockets and jewels do not appear either: they have no
 * effect in the PvP and no effect on how a ship is drawn.
 */

/** Bumped when this contract changes in a way old descriptors cannot satisfy. */
export const SKIN_VFX_FORMAT_VERSION = 1;

/* -------------------------------------------------------------------------- */
/*  Shared primitives                                                         */
/* -------------------------------------------------------------------------- */

/** `#RRGGBB`, the same notation the background contracts already use. */
export const skinColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'colour must be #RRGGBB');

/**
 * The palette of one effect: between one and four colours.
 *
 * Four is a limit rather than a preference. A particle system interpolating
 * over more than four stops stops reading as a colour and starts reading as
 * noise, and the arena already carries three meanings in colour - team, rune
 * and cannon rarity - that a skin must not compete with.
 */
export const skinPaletteSchema = z.array(skinColorSchema).min(1).max(4);

/**
 * How a particle is composited.
 *
 * Two values, and `add` is the reason the list is closed. Additive blending is
 * what makes something look like light, and it is also what turns six
 * overlapping emitters into a white blob that hides a health bar. Anything
 * exotic - multiply, erase, a custom pipeline - is absent on purpose.
 */
export const SKIN_BLEND_MODES = ['normal', 'add'] as const;
export type SkinBlendMode = (typeof SKIN_BLEND_MODES)[number];

/**
 * The particle textures a skin may use.
 *
 * A closed list of art the game already ships and already has loaded. A skin
 * cannot introduce a particle texture of its own: that would be a second asset
 * per skin with its own budget, its own hash and its own failure mode, and the
 * shapes below cover soft light, hard sparks, streaks and smoke between them.
 */
export const SKIN_PARTICLE_TEXTURES = [
  /** The hunt's own soft lozenge. What a wake is made of. */
  'projectile-streak',
  /** A small four-pointed star. Sparks and glints. */
  'star-cross',
  /** A round soft dot. Glow, embers, smoke bodies. */
  'star-point',
  /** An elongated soft streak. Speed lines. */
  'star-streak',
  /*
   * The five painted lights (gate V27A).
   *
   * The four above are procedural: a few filled circles, drawn once into a
   * texture. They are honest shapes and they cannot be neon - a bright core
   * with a saturated halo and a wide soft falloff is not something a couple of
   * `fillCircle` calls produce, and the player's word for the result was
   * "apagado".
   *
   * These are painted masters, reduced by the build. They stay a CLOSED list
   * for the reason the four above are: a skin still cannot introduce art of its
   * own. What changed is that the list now contains art worth using.
   */
  /** Near-white core, electric-cyan halo. */
  'vfx-core-cyan',
  /** White-hot core, pale-gold halo, amber bloom. */
  'vfx-core-gold',
  /** White core, violet and magenta with cyan edge accents. */
  'vfx-core-prismatic',
  /** Tintable spark: white-hot head, tapered tail. */
  'vfx-streak',
  /** Tintable ring: crisp white stroke, hollow centre, soft halo. */
  'vfx-ring',
] as const;
export type SkinParticleTexture = (typeof SKIN_PARTICLE_TEXTURES)[number];

/** A normalised offset in hull-local units. The unit is the hull's diameter. */
export const skinOffsetSchema = z
  .object({
    forward: z.number().min(-1).max(1),
    lateral: z.number().min(-1).max(1),
  })
  .strict();
export type SkinOffset = z.infer<typeof skinOffsetSchema>;

/** A closed range. Used for anything a particle system varies per particle. */
const rangeSchema = (min: number, max: number) =>
  z
    .object({ min: z.number().min(min).max(max), max: z.number().min(min).max(max) })
    .strict()
    .refine((range) => range.min <= range.max, { message: 'min must not exceed max' });

/** Alpha, always inside `0..1`, and never allowed to reach a solid 1 by range. */
export const skinAlphaRangeSchema = rangeSchema(0, 1);
/** Particle scale, as a fraction of the hull's drawn diameter. */
export const skinScaleRangeSchema = rangeSchema(0.005, 0.5);
/** Milliseconds a particle lives. Three seconds is already a long residue. */
export const skinLifespanRangeSchema = rangeSchema(60, 3_000);
/** Particle speed, in hull diameters per second. */
export const skinSpeedRangeSchema = rangeSchema(0, 6);

/* -------------------------------------------------------------------------- */
/*  Ceilings                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The hard limits. Six ships carry all of this at once in a 3x3.
 *
 * They are stated as constants rather than inlined so a reader can see the
 * whole budget in one place, and so the tests can assert the schema really
 * refuses a value above them rather than trusting a comment.
 */
export const SKIN_VFX_LIMITS = Object.freeze({
  /** Particles alive at once, per effect, per ship. */
  maxParticles: 48,
  /** Emissions per second, per effect, per ship. */
  maxFrequencyHz: 40,
  /** Distinct signature effects one skin may declare. */
  maxSignatures: 3,
  /** Magic circles on one hull. Small means few. */
  maxMagicCircles: 2,
  /** How long a motion trail may stretch, in hull diameters. */
  maxTrailLengthHulls: 4,
  /**
   * The largest a magic circle may be drawn, as a fraction of the hull's own
   * drawn diameter.
   *
   * 0.36 is well inside the first band the arena has already spent: ADR 0037
   * measured the radial bands from 0.418 to 1.05 of the hull diameter as taken
   * by the team mark, the rune indicator and the health bar. A circle of
   * diameter 0.36 has a RADIUS of 0.18, which is less than half of 0.418 - so
   * it cannot reach any of them even at the largest drawn size.
   */
  maxMagicCircleDiameter: 0.36,
  /** Alpha ceiling for anything drawn over the hull. */
  maxOverlayAlpha: 0.85,
});

/* -------------------------------------------------------------------------- */
/*  The overlay: MANDATORY                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Particles or light over the ship's own body. Every enabled skin has one.
 *
 * It rides the hull: its position, rotation and scale are the ship's, so it is
 * part of the ship rather than a thing hovering near it.
 */
export const skinOverlaySchema = z
  .object({
    texture: z.enum(SKIN_PARTICLE_TEXTURES),
    colors: skinPaletteSchema,
    alpha: skinAlphaRangeSchema,
    scale: skinScaleRangeSchema,
    lifespan: skinLifespanRangeSchema,
    /** Emissions per second. */
    frequencyHz: z.number().gt(0).max(SKIN_VFX_LIMITS.maxFrequencyHz),
    speed: skinSpeedRangeSchema,
    /** Where on the hull it is emitted from, in hull-local units. */
    offset: skinOffsetSchema,
    /**
     * How far from that offset particles are born, in hull diameters.
     *
     * Bounded at half a hull so an "overlay" cannot quietly become a halo that
     * reaches the team mark's band.
     */
    spread: z.number().min(0).max(0.5),
    blend: z.enum(SKIN_BLEND_MODES),
    maxParticles: z.int().min(1).max(SKIN_VFX_LIMITS.maxParticles),
  })
  .strict()
  .refine((overlay) => overlay.alpha.max <= SKIN_VFX_LIMITS.maxOverlayAlpha, {
    message: `overlay alpha must not exceed ${SKIN_VFX_LIMITS.maxOverlayAlpha}`,
    path: ['alpha', 'max'],
  });

export type SkinOverlay = z.infer<typeof skinOverlaySchema>;

/* -------------------------------------------------------------------------- */
/*  The motion trail: MANDATORY                                               */
/* -------------------------------------------------------------------------- */

/**
 * What a skin leaves behind while it moves. Every enabled skin has one.
 *
 * The direction is taken from the ship's REAL movement, never from its heading
 * and never from a constant: a ship drifting sideways leaves its trail to the
 * side, which is what makes the effect read as motion rather than as an
 * exhaust that happens to point backwards.
 */
export const skinMotionTrailSchema = z
  .object({
    texture: z.enum(SKIN_PARTICLE_TEXTURES),
    colors: skinPaletteSchema,
    alpha: skinAlphaRangeSchema,
    scale: skinScaleRangeSchema,
    lifespan: skinLifespanRangeSchema,
    frequencyHz: z.number().gt(0).max(SKIN_VFX_LIMITS.maxFrequencyHz),
    /** Where it is emitted from. Usually behind the hull centre. */
    offset: skinOffsetSchema,
    /**
     * How fast the ship must actually be going before anything is emitted, in
     * hull diameters per second.
     *
     * Above zero, always. A trail that emits while a ship is parked is not a
     * motion trail, and six parked ships would each be spilling particles into
     * an arena where nothing is happening.
     */
    minSpeedHullsPerSecond: z.number().gt(0).max(4),
    /** How far the residue may stretch behind the ship. */
    maxLengthHulls: z.number().gt(0).max(SKIN_VFX_LIMITS.maxTrailLengthHulls),
    blend: z.enum(SKIN_BLEND_MODES),
    maxParticles: z.int().min(1).max(SKIN_VFX_LIMITS.maxParticles),
    /**
     * What happens when the room MOVES the ship rather than the ship moving.
     *
     * A teleport and an authoritative correction are both discontinuities: the
     * ship was there and is now here, and it did not travel between them. A
     * trail that treated the gap as movement would draw a line across the arena
     * that no ship ever flew - which is a lie about where somebody was, in a
     * mode where that matters.
     *
     * `clear` drops every live particle. `hold` keeps what is already in the
     * air and emits nothing for the jump. Neither may INTERPOLATE, and there is
     * no third option.
     */
    onDiscontinuity: z.enum(['clear', 'hold']),
  })
  .strict();

export type SkinMotionTrail = z.infer<typeof skinMotionTrailSchema>;

/* -------------------------------------------------------------------------- */
/*  The signature: optional, closed vocabulary                                */
/* -------------------------------------------------------------------------- */

/**
 * A small magic circle, ON the hull.
 *
 * ## What makes it "small" and "on the body"
 *
 * Three things, all enforced rather than described:
 *
 * 1. its diameter is at most {@link SKIN_VFX_LIMITS.maxMagicCircleDiameter} of
 *    the hull's own drawn diameter;
 * 2. its centre is inside the hull - the schema refuses a centre further out
 *    than a quarter hull;
 * 3. `encircles` does not exist as an option. A ring AROUND the ship is exactly
 *    what this must not be, so there is no field that could ask for one.
 *
 * Together those keep it out of the radial bands ADR 0037 measured as spent:
 * the team mark at 0.58, the rune indicator from 0.66 and the health bar at
 * 0.88 of the hull diameter. A circle of diameter 0.36 centred a quarter hull
 * off reaches 0.43 at its furthest - and that is the worst case the schema
 * permits, with the default centre of zero reaching 0.18.
 *
 * ## It lights the art
 *
 * It is drawn ABOVE the hull and BELOW the cannon, so it reads as something
 * glowing on the ship's plating rather than as a decal floating over the whole
 * object. It never sits above the health bar, the team mark, the rune indicator
 * or any other authoritative signal.
 */
export const skinMagicCircleSchema = z
  .object({
    kind: z.literal('small_magic_circle'),
    colors: skinPaletteSchema,
    /** As a fraction of the hull's drawn diameter. */
    diameter: z.number().gt(0).max(SKIN_VFX_LIMITS.maxMagicCircleDiameter),
    /** Where its centre sits on the hull. Zero is the hull centre. */
    center: skinOffsetSchema,
    alpha: skinAlphaRangeSchema,
    /**
     * Turns per second. Slow, or not at all.
     *
     * Bounded at a quarter turn per second because a fast circle is a
     * distraction in a mode where a player is reading six ships, and because
     * anything faster starts to strobe at 60 Hz.
     */
    rotationsPerSecond: z.number().min(0).max(0.25),
    /** How many. Small means few. */
    count: z.int().min(1).max(SKIN_VFX_LIMITS.maxMagicCircles),
  })
  .strict()
  .refine((circle) => circle.alpha.max <= SKIN_VFX_LIMITS.maxOverlayAlpha, {
    message: 'a magic circle must not be opaque over the hull',
    path: ['alpha', 'max'],
  })
  .refine((circle) => Math.hypot(circle.center.forward, circle.center.lateral) <= 0.25, {
    message: 'a magic circle sits on the body: its centre must be within a quarter hull',
    path: ['center'],
  })
  .refine(
    (circle) =>
      circle.diameter / 2 + Math.hypot(circle.center.forward, circle.center.lateral) < 0.418,
    {
      message:
        'a magic circle must stay inside the hull band: the team mark, the rune indicator and the health bar own everything from 0.418 of the hull diameter outwards',
      path: ['diameter'],
    },
  );

export type SkinMagicCircle = z.infer<typeof skinMagicCircleSchema>;

/** The parameters every non-circle signature shares. */
const signatureCommon = {
  colors: skinPaletteSchema,
  alpha: skinAlphaRangeSchema,
  scale: skinScaleRangeSchema,
  lifespan: skinLifespanRangeSchema,
  frequencyHz: z.number().gt(0).max(SKIN_VFX_LIMITS.maxFrequencyHz),
  offset: skinOffsetSchema,
  blend: z.enum(SKIN_BLEND_MODES),
  maxParticles: z.int().min(1).max(SKIN_VFX_LIMITS.maxParticles),
};

/**
 * A glyph painted on the hull: a mark, a sigil, a crest.
 *
 * Static art rather than particles, which is why it has no lifespan or
 * frequency. The texture comes from the closed particle list for the same
 * reason everything else does.
 */
export const skinBodyGlyphSchema = z
  .object({
    kind: z.literal('body_glyph'),
    texture: z.enum(SKIN_PARTICLE_TEXTURES),
    colors: skinPaletteSchema,
    alpha: skinAlphaRangeSchema,
    scale: skinScaleRangeSchema,
    offset: skinOffsetSchema,
    blend: z.enum(SKIN_BLEND_MODES),
  })
  .strict();

const namedParticleSignature = <K extends string>(kind: K) =>
  z
    .object({
      kind: z.literal(kind),
      texture: z.enum(SKIN_PARTICLE_TEXTURES),
      ...signatureCommon,
    })
    .strict();

/**
 * The whole signature vocabulary, and it is closed.
 *
 * `custom_particles` is the escape hatch and it is deliberately not one: it
 * carries exactly the same validated parameters as every other entry and adds
 * only a label. There is no field in it for a texture the game does not ship,
 * a value above a ceiling, or anything that could be executed. A skin that
 * needs something genuinely new needs a new member of this union and the review
 * that comes with it.
 */
export const skinSignatureSchema = z.discriminatedUnion('kind', [
  skinBodyGlyphSchema,
  skinMagicCircleSchema,
  namedParticleSignature('flame'),
  namedParticleSignature('rainbow_trail'),
  namedParticleSignature('energy_pulse'),
  namedParticleSignature('sparks'),
  namedParticleSignature('smoke'),
  namedParticleSignature('custom_particles'),
]);

export type SkinSignature = z.infer<typeof skinSignatureSchema>;

export const SKIN_SIGNATURE_KINDS = [
  'body_glyph',
  'small_magic_circle',
  'flame',
  'rainbow_trail',
  'energy_pulse',
  'sparks',
  'smoke',
  'custom_particles',
] as const;
export type SkinSignatureKind = (typeof SKIN_SIGNATURE_KINDS)[number];

/* -------------------------------------------------------------------------- */
/*  Modality profiles and LOD                                                 */
/* -------------------------------------------------------------------------- */

/**
 * How much of the composition a given mode draws.
 *
 * A profile may SCALE what is already declared and may drop SECONDARY things.
 * It may not add anything, change a colour or introduce a new effect, because
 * then the 3x3 version would be a different skin - and a player who paid for
 * one look must recognise it in every mode.
 */
export const skinModalityProfileSchema = z
  .object({
    /**
     * Multiplier on every declared emission rate. Never above 1.
     *
     * A profile can only ever ask for LESS than the descriptor declared, which
     * is what keeps the ceilings in this file total: no combination of
     * descriptor and profile can exceed a limit the descriptor already passed.
     */
    frequencyScale: z.number().gt(0).max(1),
    /** Multiplier on every declared lifespan. Never above 1. */
    lifespanScale: z.number().gt(0).max(1),
    /** Multiplier on every declared particle ceiling. Never above 1. */
    particleScale: z.number().gt(0).max(1),
    /**
     * Which signatures this mode draws.
     *
     * A subset of the declared ones, by kind. Emptying it is allowed - a dense
     * 3x3 may legitimately keep only the overlay and the trail - but the
     * overlay and the trail themselves can never be dropped, which is why
     * neither appears here.
     */
    signatures: z.array(z.enum(SKIN_SIGNATURE_KINDS)),
  })
  .strict();

export type SkinModalityProfile = z.infer<typeof skinModalityProfileSchema>;

/**
 * Levels of detail, from the richest to the cheapest.
 *
 * Chosen by the RENDERER from a measured budget, never by the descriptor and
 * never by a player setting in this gate. `minimal` is the floor and it still
 * carries the overlay and the trail, because those are what make a skin a skin.
 */
export const SKIN_LOD_LEVELS = ['full', 'reduced', 'minimal'] as const;
export type SkinLodLevel = (typeof SKIN_LOD_LEVELS)[number];

export const skinLodSchema = z
  .object({
    full: skinModalityProfileSchema,
    reduced: skinModalityProfileSchema,
    minimal: skinModalityProfileSchema,
  })
  .strict()
  .refine(
    (lod) =>
      lod.full.frequencyScale >= lod.reduced.frequencyScale &&
      lod.reduced.frequencyScale >= lod.minimal.frequencyScale,
    { message: 'LOD levels must not get more expensive as they get cheaper', path: ['reduced'] },
  )
  .refine(
    (lod) =>
      lod.full.particleScale >= lod.reduced.particleScale &&
      lod.reduced.particleScale >= lod.minimal.particleScale,
    { message: 'LOD levels must not raise the particle ceiling', path: ['reduced'] },
  );

export type SkinLod = z.infer<typeof skinLodSchema>;

export const SKIN_MODALITIES = ['hunt', 'pvp1x1', 'pvp3x3'] as const;
export type SkinModality = (typeof SKIN_MODALITIES)[number];

/* -------------------------------------------------------------------------- */
/*  The descriptor                                                            */
/* -------------------------------------------------------------------------- */

export const skinVfxDescriptorSchema = z
  .object({
    formatVersion: z.literal(SKIN_VFX_FORMAT_VERSION),
    /** The definition this describes. Must match the manifest entry. */
    definitionId: z.string().min(3).max(64),
    /** MANDATORY: particles or light over the body. */
    overlay: skinOverlaySchema,
    /** MANDATORY: what it leaves behind while moving. */
    trail: skinMotionTrailSchema,
    /** Optional, and closed. At most three. */
    signatures: z.array(skinSignatureSchema).max(SKIN_VFX_LIMITS.maxSignatures).default([]),
    /** The three modes, each naming which signatures it draws. */
    hunt: skinModalityProfileSchema,
    pvp1x1: skinModalityProfileSchema,
    pvp3x3: skinModalityProfileSchema,
    lod: skinLodSchema,
    /**
     * What is drawn when a profile cannot be resolved.
     *
     * Never "nothing" and never "whatever was last used": an explicit level, so
     * a missing profile produces a skin that is recognisably itself rather than
     * a bare hull that looks like somebody was refunded.
     */
    fallbackLod: z.enum(SKIN_LOD_LEVELS),
  })
  .strict()
  .superRefine((descriptor, ctx) => {
    const declared = new Set(descriptor.signatures.map((signature) => signature.kind));
    for (const modality of SKIN_MODALITIES) {
      for (const kind of descriptor[modality].signatures) {
        if (!declared.has(kind)) {
          ctx.addIssue({
            code: 'custom',
            path: [modality, 'signatures'],
            message: `${modality} draws "${kind}", which this skin does not declare`,
          });
        }
      }
    }
    const kinds = descriptor.signatures.map((signature) => signature.kind);
    if (new Set(kinds).size !== kinds.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['signatures'],
        message: 'a skin may declare each signature kind at most once',
      });
    }
    /*
     * The 3x3 may be cheaper. It may not be a different skin.
     *
     * "Reduce the secondary" is the whole permission, and the check that gives
     * it teeth is this one: the dense mode cannot ask for MORE than the 1x1 on
     * any axis. Without it, a descriptor could make the 3x3 the loud version
     * and the 1x1 the quiet one, which is exactly backwards.
     */
    if (
      descriptor.pvp3x3.frequencyScale > descriptor.pvp1x1.frequencyScale ||
      descriptor.pvp3x3.particleScale > descriptor.pvp1x1.particleScale ||
      descriptor.pvp3x3.lifespanScale > descriptor.pvp1x1.lifespanScale
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['pvp3x3'],
        message: 'the 3x3 profile must not be denser than the 1x1',
      });
    }
  });

export type SkinVfxDescriptor = z.infer<typeof skinVfxDescriptorSchema>;
